import { useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Minus, Phone, Plus } from 'lucide-react'
import apiClient from '@/api/client'
import type { OnlineOrder, OnlineOrderEditPreview, OnlineOrderEditReason, OnlineOrderRemoval } from '@/types'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'

const REASONS: [OnlineOrderEditReason, string][] = [
  ['out_of_stock', 'Out of stock'],
  ['customer_request', 'Customer asked'],
  ['other', 'Other'],
]

// EditOnlineOrderDialog — F-FE-N193. Remove items from an order still in the
// inbox, after staff have phoned the customer and they agreed. Remove only:
// each line can be taken off in full or in part, never added to. Totals come
// from the server (dry_run) so the promo/minimum rules match what is saved.
export function EditOnlineOrderDialog({
  order,
  onClose,
  formatCurrency,
  pendingQueryKey,
}: {
  order: OnlineOrder | null
  onClose: () => void
  formatCurrency: (n: number) => string
  pendingQueryKey: readonly unknown[]
}) {
  const queryClient = useQueryClient()
  // How many to remove from each cart line, by line index.
  const [take, setTake] = useState<Record<number, number>>({})
  const [reason, setReason] = useState<OnlineOrderEditReason>('out_of_stock')
  const [note, setNote] = useState('')
  const [hide, setHide] = useState<Set<string>>(new Set())
  const [confirmBelowMin, setConfirmBelowMin] = useState(false)
  // Set when another till changed this order's items while the dialog was open.
  const [changedElsewhere, setChangedElsewhere] = useState(false)

  // `order` is the live inbox row, so it is a new object on every poll. Reset
  // only when a different order opens (everything) or this order's items
  // change underneath us (the picks, which may no longer point at the right
  // lines) — never on a plain refetch, which would wipe what staff tapped.
  const orderId = order?.id ?? null
  const cartSig = order ? JSON.stringify(order.cart) : ''
  const seen = useRef<{ id: string | null; sig: string }>({ id: null, sig: '' })
  useEffect(() => {
    const prev = seen.current
    seen.current = { id: orderId, sig: cartSig }
    if (!orderId) return
    if (prev.id !== orderId) {
      setReason('out_of_stock')
      setNote('')
      setChangedElsewhere(false)
    } else if (prev.sig === cartSig) {
      return
    } else {
      setChangedElsewhere(true)
    }
    setTake({})
    setHide(new Set())
    setConfirmBelowMin(false)
  }, [orderId, cartSig])

  const removals: OnlineOrderRemoval[] = useMemo(() => {
    if (!order) return []
    return Object.entries(take)
      .filter(([, n]) => n > 0)
      .map(([i, n]) => ({ line: Number(i), product_id: order.cart[Number(i)].product_id, quantity: n }))
      .sort((a, b) => a.line - b.line)
  }, [order, take])

  // Products being removed in full — the only ones it makes sense to hide.
  const removedProducts = useMemo(() => {
    if (!order) return []
    const seen = new Map<string, string>()
    for (const r of removals) {
      if (!seen.has(r.product_id)) seen.set(r.product_id, order.cart[r.line].name)
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }))
  }, [order, removals])

  const removesEverything =
    !!order && order.cart.every((line, i) => (take[i] ?? 0) >= line.quantity)

  const previewQuery = useQuery({
    queryKey: ['online-orders', 'edit-preview', order?.id, removals],
    queryFn: () =>
      apiClient
        .editOnlineOrder(order!.id, { removals, reason_code: reason, dry_run: true })
        .then((r) => r.data as OnlineOrderEditPreview),
    enabled: !!order && removals.length > 0 && !removesEverything,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  })
  const preview = removals.length > 0 && !removesEverything ? previewQuery.data : undefined

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.editOnlineOrder(order!.id, {
        removals,
        reason_code: reason,
        note: note.trim() || undefined,
        hide_product_ids: [...hide].filter((id) => removedProducts.some((p) => p.id === id)),
        confirm_below_minimum: confirmBelowMin,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: pendingQueryKey })
      const d = res.data
      const parts = [d ? `New total ${formatCurrency(d.total_amount)}.` : '']
      if (d?.promo_code_dropped) parts.push(`Promo ${d.promo_code_dropped} no longer applies and was removed.`)
      if (d?.hidden_product_ids?.length) parts.push('Hidden from the online menu until you turn it back on in Menu.')
      toastHelpers.success('Order updated', parts.filter(Boolean).join(' '))
      onClose()
    },
    onError: (err: unknown) => {
      const e = err as { message?: string; code?: string }
      if (e?.code === 'online_order_not_pending' || e?.code === 'cart_changed') {
        queryClient.invalidateQueries({ queryKey: pendingQueryKey })
        toastHelpers.error('Order changed', e.message || 'Someone else updated this order. Open it again.')
        onClose()
        return
      }
      if (e?.code === 'below_minimum_confirm') {
        toastHelpers.error('Below the minimum order', 'Tick the box to confirm the customer agreed.')
        return
      }
      toastHelpers.error('Could not update order', e?.message || 'Please try again.')
    },
  })

  const setLineTake = (i: number, n: number) => {
    if (!order) return
    const clamped = Math.max(0, Math.min(order.cart[i].quantity, n))
    setTake((cur) => ({ ...cur, [i]: clamped }))
  }

  const busy = saveMutation.isPending
  const needsMinConfirm = !!preview?.below_minimum && !confirmBelowMin
  const canSave = removals.length > 0 && !removesEverything && !!preview && !needsMinConfirm && !busy

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && !busy && onClose()}>
      {/* Pinned near the top rather than centred: the dialog grows as items are
          removed (reason, totals, warnings), and a centred one shifts every
          row up under the next tap. */}
      <DialogContent className="top-[6dvh] translate-y-0">
        <DialogHeader>
          <DialogTitle>Remove items</DialogTitle>
          <DialogDescription>
            {order?.customer_name || 'Guest'}
            {order?.customer_phone && (
              <>
                {' · '}
                <a href={`tel:${order.customer_phone}`} className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline">
                  <Phone className="h-3 w-3" />
                  {order.customer_phone}
                </a>
              </>
            )}
            . Call the customer first, then remove what you can’t send.
          </DialogDescription>
        </DialogHeader>

        {changedElsewhere && (
          <Warning>This order was just changed on another screen. Check the items again.</Warning>
        )}

        <ul className="divide-y divide-border rounded-lg border border-border">
          {order?.cart.map((line, i) => {
            const n = take[i] ?? 0
            const gone = n >= line.quantity
            return (
              <li key={i} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p className={`truncate text-sm ${gone ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    <span className="font-medium tabular-nums">{line.quantity}×</span> {line.name}
                  </p>
                  {(line.modifier_labels?.length ?? 0) > 0 && (
                    <p className="truncate text-xs text-muted-foreground">{line.modifier_labels!.join(', ')}</p>
                  )}
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {formatCurrency(line.unit_price)} each
                    {n > 0 && !gone && <span className="text-danger-fg"> · removing {n}</span>}
                  </p>
                </div>
                {line.quantity === 1 ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLineTake(i, gone ? 0 : 1)}
                    disabled={busy}
                    className={gone ? '' : 'text-red-600 hover:bg-danger-surface hover:text-danger-fg'}
                  >
                    {gone ? 'Undo' : 'Remove'}
                  </Button>
                ) : (
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setLineTake(i, n + 1)}
                      disabled={busy || gone}
                      aria-label={`Remove one ${line.name}`}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </Button>
                    <span className="w-16 text-center text-xs tabular-nums text-muted-foreground">
                      send {line.quantity - n}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setLineTake(i, n - 1)}
                      disabled={busy || n === 0}
                      aria-label={`Put one ${line.name} back`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>

        {removesEverything && (
          <p className="rounded-lg bg-danger-surface px-3 py-2 text-sm text-danger-fg">
            That removes everything. Close this and reject the order instead.
          </p>
        )}

        {removals.length > 0 && !removesEverything && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {REASONS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setReason(value)}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                    reason === value
                      ? 'border-primary bg-primary/10 font-medium text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional), e.g. customer agreed on the phone"
              maxLength={200}
              rows={2}
            />

            {reason === 'out_of_stock' &&
              removedProducts.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                  <Checkbox
                    checked={hide.has(p.id)}
                    onCheckedChange={(on) =>
                      setHide((cur) => {
                        const next = new Set(cur)
                        if (on) next.add(p.id)
                        else next.delete(p.id)
                        return next
                      })
                    }
                  />
                  Also hide {p.name} from the online menu
                </label>
              ))}

            <div className="space-y-1 rounded-lg bg-muted/50 px-3 py-2 text-sm">
              {preview ? (
                <>
                  <Row label="Subtotal" value={formatCurrency(preview.subtotal)} />
                  {preview.discount_amount > 0 && (
                    <Row label="Discount" value={`−${formatCurrency(preview.discount_amount)}`} />
                  )}
                  {preview.service_charge_amount > 0 && (
                    <Row label="Service charge" value={formatCurrency(preview.service_charge_amount)} />
                  )}
                  {preview.tax_amount > 0 && <Row label="Tax" value={formatCurrency(preview.tax_amount)} />}
                  {preview.delivery_fee_amount > 0 && (
                    <Row label="Delivery" value={formatCurrency(preview.delivery_fee_amount)} />
                  )}
                  <div className="flex justify-between gap-2 border-t border-border pt-1 font-semibold text-foreground">
                    <span>New total</span>
                    <span className="tabular-nums">
                      <span className="mr-2 font-normal text-muted-foreground line-through">
                        {formatCurrency(preview.total_before)}
                      </span>
                      {formatCurrency(preview.total_amount)}
                    </span>
                  </div>
                </>
              ) : previewQuery.isError ? (
                <p className="text-danger-fg">
                  {(previewQuery.error as { message?: string })?.message || 'Could not price this change.'}
                </p>
              ) : (
                <p className="text-muted-foreground">Working out the new total…</p>
              )}
            </div>

            {preview?.promo_code_dropped && (
              <Warning>
                The order no longer reaches the minimum for promo {preview.promo_code_dropped}, so the discount comes off.
                Tell the customer.
              </Warning>
            )}
            {preview?.below_minimum && (
              <Warning>
                <span className="block">
                  This is below the minimum order of {formatCurrency(preview.minimum_order)}.
                </span>
                <label className="mt-1.5 flex cursor-pointer items-center gap-2 font-medium">
                  <Checkbox checked={confirmBelowMin} onCheckedChange={setConfirmBelowMin} />
                  The customer agreed. Go ahead anyway.
                </label>
              </Warning>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={!canSave}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 rounded-lg bg-warning-surface px-3 py-2 text-sm text-warning-fg">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{children}</div>
    </div>
  )
}
